import moment from "moment";

import {
	Address,
	Name,
	ZomentumEntities,
	TableQueryFilter,
	ArchievedWithSyncModel,
	CommonServerStatusEnum
} from "../common";
import { ChildEntityHydrationParams } from "../Utils";
import { Integrations, CustomField } from "../Settings";
import { Opportunity } from "../Opportunity";
import { UserData } from "../Users";
import { Document } from "../Documents";
import { capitalizeFirstLetter } from "../Utils";

export class ClientCount {
	all = 0;
}

export interface ClientContactCount extends ClientCount {}

export interface ChildEntityUsers {
	data: ClientContact[];
}
export interface ChildEntityOpportunities {
	data: Opportunity[];
}
export interface ChildEntityDocuments {
	data: Document[];
}

export enum ClientType {
	Prospect = "Prospect",
	Customer = "Customer",
	Owner = "Owner",
	Partner = "Partner",
	Vendor = "Vendor",
	Other = "Other",
	Reseller = "Reseller"
}

export const clientCompanyTypeOptions = [
	{
		name: "Prospect",
		value: ClientType.Prospect
	},
	{
		name: "Customer",
		value: ClientType.Customer
	},
	{
		name: "Owner",
		value: ClientType.Owner
	},
	{
		name: "Partner",
		value: ClientType.Partner
	},
	{
		name: "Vendor",
		value: ClientType.Vendor
	},
	{
		name: "Reseller",
		value: ClientType.Reseller
	},
	{
		name: "Other",
		value: ClientType.Other
	}
];

export class Client extends ArchievedWithSyncModel {
	billing_address: Address | null = new Address();
	ship_to_address: Address | null = new Address();
	client_user_count = 0;
	client_users: ChildEntityUsers | null = null;
	campaign: string | null = null;
	source: string | null = null;
	company_id = "";
	company_type: ClientType | null = ClientType.Prospect;
	company_types: ClientType[] = new Array<ClientType>();
	created: Date = new Date();
	fax = "";
	website = "";
	linked_in_url: string | null = null;
	facebook_url: string | null = null;
	twitter_url: string | null = null;
	market: string | null = null;
	date_acquired: Date | null = null;
	id = "";
	integrations: Integrations = new Integrations();
	logo = "";
	name = "";
	opportunities: ChildEntityOpportunities | null = null;
	opportunity_count = 0;
	owner_user_id = "";
	partner_id = "";
	read_only = false;
	shipping_addresses: Address[] | null = null;
	updated: Date = new Date();
	tax_region_id: string | null = null;
	custom_fields?: CustomField[];
	owner_user: UserData | null = null;
	owner_id?: string;
	documents: ChildEntityDocuments | null = null;
	ms365_integration?: Ms365IntegrationDetails;
}

export class Ms365IntegrationDetails {
	status: Ms365IntegrationStatus | null = null;
	name = "";
	userName = "";
	email = "";
	requested_timestamp: Date = new Date();
	connected_timestamp: Date = new Date();
}

export enum Ms365IntegrationStatus {
	REQUESTED = "requested",
	CONNECTED = "connected"
}

export type ClientSource =
	| "self"
	| "autotask"
	| "connectwise"
	| "all"
	| "kaseya";

export class ClientsRequest {
	filters: TableQueryFilter[];
	keywords: string | null;
	limit: number | null;
	includeEntities: ChildEntityHydrationParams[];
	sync_source?: ClientSource | null;
	selected_id?: string | null;
	page?: number;

	constructor(
		filters: TableQueryFilter[],
		keywords: string | null,
		limit: number | null,
		includeEntities: ChildEntityHydrationParams[],
		sync_source?: ClientSource | null,
		selected_id?: string | null,
		page?: number
	) {
		this.filters = filters;
		this.keywords = keywords;
		this.limit = limit;
		this.includeEntities = includeEntities;
		this.sync_source = sync_source;
		this.selected_id = selected_id;
		this.page = page;
	}
}

export class ClientsGlobalSearchRequest {
	value: string;
	limit: number | null;
	constructor(value: string, limit: number | null) {
		this.value = value;
		this.limit = limit;
	}
}

export enum ClientUserContactType {
	PrimaryContact = "primary_contact",
	SecondaryContact = "secondary_contact"
}

export enum ClientMarket {
	Agriculture = "Agriculture",
	ArchitectEngineer = "Architect / Engineer",
	Automotive = "Automotive",
	Communications = "Communications",
	ConstructionDevelopment = "Construction / Development",
	Education = "Education",
	Financial = "Financial",
	Government = "Government",
	Healthcare = "Healthcare",
	HumanResources = "Human Resources",
	Insurance = "Insurance",
	Internet = "Internet",
	Legal = "Legal",
	Manufacturing = "Manufacturing",
	MediaAdvertising = "Media / Advertising",
	NonProfit = "NonProfit",
	RealEstate = "Real Estate",
	RestaurantBar = "Restaurant / Bar",
	Retail = "Retail",
	Services = "Services",
	Transportation = "Transportation",
	WholesaleDistribution = "Wholesale / Distribution",
	Other = "Other"
}

export const clientMarketArray: string[] = [
	ClientMarket.Agriculture,
	ClientMarket.ArchitectEngineer,
	ClientMarket.Automotive,
	ClientMarket.Communications,
	ClientMarket.ConstructionDevelopment,
	ClientMarket.Education,
	ClientMarket.Financial,
	ClientMarket.Government,
	ClientMarket.Healthcare,
	ClientMarket.HumanResources,
	ClientMarket.Insurance,
	ClientMarket.Internet,
	ClientMarket.Legal,
	ClientMarket.Manufacturing,
	ClientMarket.MediaAdvertising,
	ClientMarket.NonProfit,
	ClientMarket.RealEstate,
	ClientMarket.RestaurantBar,
	ClientMarket.Retail,
	ClientMarket.Services,
	ClientMarket.Transportation,
	ClientMarket.WholesaleDistribution,
	ClientMarket.Other
];

export const clientTypeArray = [
	ClientType.Prospect,
	ClientType.Customer,
	ClientType.Owner,
	ClientType.Partner,
	ClientType.Vendor,
	ClientType.Other
];

export const defaultClientFields = [
	{ display_value: "Company", id_value: "name" },
	{
		display_value: "Tax Region",
		id_value: "tax_region_id"
	},
	{ display_value: "Phone", id_value: "phone" },
	{ display_value: "Owner", id_value: "owner_user_id" },
	{ display_value: "Fax", id_value: "fax" },
	{ display_value: "Website", id_value: "website" },
	{
		display_value: "LinkedIn URL",
		id_value: "linked_in_url"
	},
	{
		display_value: "Facebook URL",
		id_value: "facebook_url"
	},
	{
		display_value: "Twitter URL",
		id_value: "twitter_url"
	},
	{ display_value: "Market", id_value: "market" },
	{
		display_value: "Company Type",
		id_value: "company_types"
	},
	{ display_value: "Source", id_value: "source" },
	{ display_value: "Campaign", id_value: "campaign" },
	{
		display_value: "Date Acquired",
		id_value: "date_acquired"
	},
	{ display_value: "Country", id_value: "country" },
	{ display_value: "State", id_value: "state" },
	{ display_value: "City", id_value: "city" },
	{ display_value: "Street", id_value: "address_line_1" },
	{ display_value: "Street 2", id_value: "address_line_2" },
	{ display_value: "Zip Code", id_value: "pincode" },
	{
		display_value: "Shipping Country",
		id_value: "ship_to_country"
	},
	{
		display_value: "Shipping State",
		id_value: "ship_to_state"
	},
	{
		display_value: "Shipping City",
		id_value: "ship_to_city"
	},
	{
		display_value: "Shipping Street",
		id_value: "ship_to_address_line_1"
	},
	{
		display_value: "Shipping Street 2",
		id_value: "ship_to_address_line_2"
	},
	{
		display_value: "Shipping Zip Code",
		id_value: "ship_to_pincode"
	}
];

export const getSelectableFieldsForBulkUpdateClient = (
	customFields: CustomField[]
) => {
	return defaultClientFields.concat([
		...customFields
			?.filter(
				customField =>
					customField.is_value_editable_from_ui !== false &&
					(!customField.system_field_id ||
						customField.system_field_id.length === 0)
			)
			?.map(customField => {
				return {
					display_value: customField.display_name,
					id_value: `custom_fields.${customField.id}`
				};
			})
	]);
};

export enum BulkUpdateClientFilterDisplayValues {
	COMPANY = "Company"
	// Add more filters here - For future reference
}

export class ClientsResponse {
	filtered_count_map: ClientCount = new ClientCount();
	data: Client[] = new Array<Client>();
}

export interface ClientsGlobalSearchResponse {
	data: Client[];
}

export class ClientFormValues {
	name = "";
	last_name = "";
	client_company_name? = "";
	email?: string | null = null;
	client_company_number?: string | null = null;
	phone: string | null = null;
	owner_user_id = "";
	fax = "";
	website = "";
	linked_in_url: string | null = null;
	facebook_url: string | null = null;
	twitter_url: string | null = null;
	market: string | null = null;
	date_acquired: moment.Moment = moment();
	company_type: string = ClientType.Prospect;
	campaign: string | null = null;
	source: string | null = null;
	country: string | null = null;
	state: string | null = null;
	city: string | null = null;
	address_line_1: string | null = null;
	address_line_2: string | null = null;
	pincode: string | null = null;
	use_same_address = false;
	ship_to_country = "";
	ship_to_state = "";
	ship_to_city = "";
	ship_to_address_line_1 = "";
	ship_to_address_line_2 = "";
	ship_to_pincode = "";
	tax_region_id: string | null = null;
	custom_field_form_values: Record<string, string> = {};
	employee_size?: number | null = null;
	mobile?: string | null = null;
	shipping_addresses?: Address[] | null = null;
}

export class AddClientRequest {
	billing_address: Address | null = new Address();
	company_type: string | null = ClientType.Prospect;
	name = "";
	last_name?: string = "";
	owner_user_id = "";
	fax = "";
	website = "";
	linked_in_url: string | null = null;
	facebook_url: string | null = null;
	twitter_url: string | null = null;
	market?: string | null = null;
	date_acquired: Date | null = null;
	campaign: string | null = null;
	source: string | null = null;
	tax_region_id: string | null = null;
	custom_fields?: CustomField[] = new Array<CustomField>();
	mandatory_custom_field_check_require = false;
	shipping_addresses: Address[] = new Array<Address>();
	email?: string | null = null;
	client_company_name?: string | null = null;
}

export class EditClientRequest extends AddClientRequest {
	id = "";
	includedChildEntities: ChildEntityHydrationParams[] =
		new Array<ChildEntityHydrationParams>();
}

export class ClientContactFormValues {
	first_name = "";
	last_name = "";
	email: string | null = "";
	phone = "";
	mobile?: string;
	contact_type: ClientUserContactType =
		ClientUserContactType.SecondaryContact;
	department = "";
	job_title = "";
	custom_field_form_values: Record<string, string> = {};
}

export class AddClientContactRequest {
	client_company_id = "";
	department = "";
	job_title = "";
	name: Name = new Name();
	email: string | null = "";
	phone = "";
	mobile?: string;
	contact_type: ClientUserContactType =
		ClientUserContactType.SecondaryContact;
	additional_emails: Array<string> = new Array<string>();
	custom_fields?: CustomField[] = new Array<CustomField>();
	mandatory_custom_field_check_require = false;
}

export class ClientContactRequest {
	filters: TableQueryFilter[] = new Array<TableQueryFilter>();
	limit = 50;
	page = 1;
	clientId = "";
	keywords?: string = "";
	selected_id?: string[] = [];
	countQueryFlag?: boolean = false;
	sortQuery?: string = "";
	localeQuery?: string = "";
	includeChildEntities?: ChildEntityHydrationParams[] = [];
	constructor(id: string) {
		this.clientId = id;
	}
}

export class ClientContactsResponse {
	data: ClientContact[] = new Array<ClientContact>();
	filtered_count_map?: ClientContactCount;
}

export class ClientContact extends ArchievedWithSyncModel {
	additionalEmails: string[] = new Array<string>();
	client_company_id = "";
	client_company_name = "";
	client_user_owner = "";
	company_id = "";
	contact_type?: ClientUserContactType | null = null;
	created: Date = new Date();
	department = "";
	email: string | null = "";
	entity_type: ZomentumEntities | null = null;
	id = "";
	job_title = "";
	name: Name = new Name();
	phone?: string;
	mobile?: string;
	updated: Date = new Date();
	custom_fields?: CustomField[] = new Array<CustomField>();
	read_only?: boolean;
	mandatory_custom_field_check_require = false;

	public static getFullName = (contact: ClientContact): string => {
		return capitalizeFirstLetter(contact.name.full);
	};

	public static getFullNameWithEmail = (contact: ClientContact): string => {
		const fullName = ClientContact.getFullName(contact);
		return `${fullName} ${
			!!contact.email?.length ? `<${contact.email}>` : ""
		}`;
	};
}

export interface ClientContactBulkUpdate {
	id: string;
	entity: Omit<Partial<ClientContact>, "id">;
}

export interface ClientContactBulkUpdateRequest {
	data: ClientContactBulkUpdate[];
}

export interface ClientContactBulkUpdateResponse {
	status: CommonServerStatusEnum;
	response: ClientContact;
}

export interface ClientContactBulkUpdateServiceResponse {
	results: ClientContactBulkUpdateResponse[];
}

export interface ClientBulkUpdateResponse {
	status: CommonServerStatusEnum;
	response: Client;
}

export interface ClientBulkUpdateServiceResponse {
	results: ClientBulkUpdateResponse[];
}
